import type {
  ReportChartSpec,
  ReportSankeyLink,
  ReportSankeyNode,
  SankeyScope,
} from '../../stores/types'
import { hasFlowOfFundsRoles } from './FlowOfFundsChart/flowModel'
import { type FlowView, flowForView } from './FlowOfFundsChart/flowViews'

export type SankeyView = FlowView

export interface SankeyFlow {
  nodes: ReportSankeyNode[]
  links: ReportSankeyLink[]
}

const VIEW_LABELS: Record<SankeyView, string> = {
  all: 'All',
  external: 'Money in & out',
  internal: 'Between own accounts',
}

const EMPTY_VIEW_NOTES: Record<SankeyScope, string> = {
  external: 'No money came in from or went out to outside sources.',
  internal: "No money moved between the client's own accounts.",
}

export const DEFAULT_SANKEY_VIEW: SankeyView = 'all'

export function emptySankeyViewNote(view: SankeyView): string | null {
  return view === 'all' ? null : EMPTY_VIEW_NOTES[view]
}

export function sankeyViewLabel(view: SankeyView): string {
  return VIEW_LABELS[view]
}

export function filterSankeyByView(
  flow: SankeyFlow,
  view: SankeyView,
): SankeyFlow {
  if (view === 'all') return flow
  const links = flow.links.filter((link) => link.scope === view)
  const touchedNames = new Set(
    links.flatMap((link) => [link.source, link.target]),
  )
  const nodes = flow.nodes.filter((node) => touchedNames.has(node.name))
  return { nodes, links }
}

// The flow of funds chart always offers every view, since an empty view
// is itself a finding. Other charts get a switch only when both scopes
// have links, or every view would look the same.
export function availableSankeyViews(flow: SankeyFlow): SankeyView[] {
  if (hasFlowOfFundsRoles(flow.nodes)) return ['all', 'external', 'internal']
  const scopedViews = (['external', 'internal'] as const).filter((view) =>
    flow.links.some((link) => link.scope === view),
  )
  if (scopedViews.length < 2) return []
  return ['all', ...scopedViews]
}

export function chartSpecForView(
  spec: ReportChartSpec,
  view: SankeyView,
): ReportChartSpec {
  if (spec.type !== 'sankey' || view === 'all') return spec
  const flow = { nodes: spec.nodes ?? [], links: spec.links ?? [] }
  const shown = hasFlowOfFundsRoles(flow.nodes)
    ? flowForView(flow, view)
    : filterSankeyByView(flow, view)
  return { ...spec, ...shown }
}

export function isSankeyView(value: unknown): value is SankeyView {
  return typeof value === 'string' && Object.keys(VIEW_LABELS).includes(value)
}
